/**
 * MEDIA-EDITOR-PROPERTY-SURFACES-A1 — browser decode + bounded JPEG export
 * for Property photo/plan surfaces.
 */

import { getExportCropRect } from "./propertyImageModel";

export const PHOTO_MAX_OUTPUT_SIDE = 1920;
export const PLAN_MAX_OUTPUT_SIDE = 2560;
export const MESSAGE_MAX_OUTPUT_SIDE = 1600;
export const COMPANY_BANNER_MAX_OUTPUT_SIDE = 1920;
export const JPEG_QUALITY = 0.9;

export class PropertyImageExportError extends Error {
  constructor(code, userMessage) {
    super(userMessage);
    this.name = "PropertyImageExportError";
    this.code = code;
    this.userMessage = userMessage;
  }
}

export const EXPORT_DECODE_ERROR = "Не вдалося прочитати це зображення.";
export const EXPORT_DIMENSION_ERROR = "Невірні розміри зображення.";
export const EXPORT_EXPORT_ERROR = "Не вдалося експортувати зображення.";

export function maxOutputSideForKind(kind) {
  if (kind === "plan") return PLAN_MAX_OUTPUT_SIDE;
  if (kind === "message_image") return MESSAGE_MAX_OUTPUT_SIDE;
  if (kind === "company_banner") return COMPANY_BANNER_MAX_OUTPUT_SIDE;
  return PHOTO_MAX_OUTPUT_SIDE;
}

export function buildEditedFilename(sourceName, kind) {
  let suffix = "photo-edited.jpg";
  let fallbackStem = "photo";
  if (kind === "plan") {
    suffix = "plan-edited.jpg";
    fallbackStem = "plan";
  } else if (kind === "message_image") {
    suffix = "message-edited.jpg";
    fallbackStem = "message";
  } else if (kind === "company_banner") {
    suffix = "banner-edited.jpg";
    fallbackStem = "banner";
  }
  if (!sourceName || typeof sourceName !== "string") return suffix;
  const trimmed = sourceName.trim();
  if (!trimmed) return suffix;
  const slash = Math.max(trimmed.lastIndexOf("/"), trimmed.lastIndexOf("\\"));
  const base = slash >= 0 ? trimmed.slice(slash + 1) : trimmed;
  if (!base || base === "." || base === "..") return suffix;
  const dot = base.lastIndexOf(".");
  const stem = dot > 0 ? base.slice(0, dot) : base;
  const safe = stem.trim() || fallbackStem;
  return `${safe}-${suffix}`;
}

function defaultCreateImageBitmap(blob) {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(blob);
  }
  return Promise.reject(new Error("createImageBitmap unavailable"));
}

function defaultLoadImageElement(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = url;
  });
}

async function defaultDecodeSource(file, deps) {
  const createObjectURL = deps.createObjectURL || URL.createObjectURL.bind(URL);
  const revokeObjectURL = deps.revokeObjectURL || URL.revokeObjectURL.bind(URL);
  const createImageBitmapFn = deps.createImageBitmap || defaultCreateImageBitmap;
  const loadImageElement = deps.loadImageElement || defaultLoadImageElement;

  try {
    const bitmap = await createImageBitmapFn(file);
    return {
      width: bitmap.width,
      height: bitmap.height,
      drawSource: bitmap,
      cleanup: () => {
        if (typeof bitmap.close === "function") bitmap.close();
      },
    };
  } catch (_bitmapError) {
    let objectUrl = null;
    try {
      objectUrl = createObjectURL(file);
      const img = await loadImageElement(objectUrl);
      return {
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
        drawSource: img,
        cleanup: () => {
          if (objectUrl) revokeObjectURL(objectUrl);
        },
      };
    } catch (_imgError) {
      if (objectUrl) revokeObjectURL(objectUrl);
      throw new PropertyImageExportError("decode", EXPORT_DECODE_ERROR);
    }
  }
}

function defaultCreateCanvas(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function computeOutputDimensions(cropWidth, cropHeight, maxSide) {
  const w = cropWidth;
  const h = cropHeight;
  if (!(w > 0) || !(h > 0)) return null;
  const longest = Math.max(w, h);
  if (longest <= maxSide) {
    return {
      width: Math.max(1, Math.floor(w)),
      height: Math.max(1, Math.floor(h)),
    };
  }
  const scale = maxSide / longest;
  return {
    width: Math.max(1, Math.floor(w * scale)),
    height: Math.max(1, Math.floor(h * scale)),
  };
}

function cleanupDecodedOnce(decoded) {
  if (!decoded || decoded._cleaned) return;
  decoded._cleaned = true;
  if (typeof decoded.cleanup !== "function") return;
  try {
    decoded.cleanup();
  } catch (_err) {
    // Cleanup failure must not replace the authoritative export error.
  }
}

/**
 * Export a freeform JPEG from the current crop transform.
 *
 * @param {File} file
 * @param {object} cropState
 * @param {"image"|"plan"|"message_image"|"company_banner"} kind
 * @param {object} [deps]
 */
export async function createPropertyImageFile(file, cropState, kind = "image", deps = {}) {
  if (!file) {
    throw new PropertyImageExportError("decode", EXPORT_DECODE_ERROR);
  }
  if (
    !cropState
    || !Number.isFinite(cropState.viewportWidth)
    || !Number.isFinite(cropState.viewportHeight)
    || cropState.viewportWidth <= 0
    || cropState.viewportHeight <= 0
  ) {
    throw new PropertyImageExportError("dimensions", EXPORT_DIMENSION_ERROR);
  }

  const decodeSource = deps.decodeSource || defaultDecodeSource;
  let decoded;
  try {
    decoded = await decodeSource(file, deps);
  } catch (err) {
    if (err instanceof PropertyImageExportError) throw err;
    throw new PropertyImageExportError("decode", EXPORT_DECODE_ERROR);
  }

  try {
    const rotation = normalizeRotation(cropState.rotation);
    let workingWidth = decoded.width;
    let workingHeight = decoded.height;
    let drawSource = decoded.drawSource;

    if (rotation !== 0) {
      const createCanvas = deps.createCanvas || defaultCreateCanvas;
      const rw = rotation % 180 === 0 ? decoded.width : decoded.height;
      const rh = rotation % 180 === 0 ? decoded.height : decoded.width;
      const rotatedCanvas = createCanvas(rw, rh);
      const rctx = rotatedCanvas.getContext("2d");
      if (!rctx) {
        throw new PropertyImageExportError("export", EXPORT_EXPORT_ERROR);
      }
      rctx.fillStyle = "#ffffff";
      rctx.fillRect(0, 0, rw, rh);
      rctx.translate(rw / 2, rh / 2);
      rctx.rotate((rotation * Math.PI) / 180);
      try {
        rctx.drawImage(
          decoded.drawSource,
          -decoded.width / 2,
          -decoded.height / 2,
        );
      } catch (_rotDrawError) {
        throw new PropertyImageExportError("export", EXPORT_EXPORT_ERROR);
      }
      workingWidth = rw;
      workingHeight = rh;
      drawSource = rotatedCanvas;
    }

    const sourceWidth =
      cropState.sourceWidth > 0 ? cropState.sourceWidth : workingWidth;
    const sourceHeight =
      cropState.sourceHeight > 0 ? cropState.sourceHeight : workingHeight;

    // Keep sourceWidth/Height available for getExportCropRect consumers.
    void sourceWidth;
    void sourceHeight;

    const rect = getExportCropRect(cropState);

    if (!rect || !(rect.width > 0) || !(rect.height > 0)) {
      throw new PropertyImageExportError("dimensions", EXPORT_DIMENSION_ERROR);
    }

    const maxSide = maxOutputSideForKind(kind);
    const outputDims = computeOutputDimensions(rect.width, rect.height, maxSide);
    if (!outputDims) {
      throw new PropertyImageExportError("dimensions", EXPORT_DIMENSION_ERROR);
    }

    const createCanvas = deps.createCanvas || defaultCreateCanvas;
    const canvas = createCanvas(outputDims.width, outputDims.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new PropertyImageExportError("export", EXPORT_EXPORT_ERROR);
    }

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outputDims.width, outputDims.height);

    try {
      ctx.drawImage(
        drawSource,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
        0,
        0,
        outputDims.width,
        outputDims.height,
      );
    } catch (_drawError) {
      throw new PropertyImageExportError("export", EXPORT_EXPORT_ERROR);
    }

    const quality = deps.jpegQuality != null ? deps.jpegQuality : JPEG_QUALITY;
    const toBlob =
      deps.toBlob
      || ((c, type, q) => {
        if (typeof c.toBlob !== "function") {
          return Promise.reject(new Error("canvas.toBlob unavailable"));
        }
        return new Promise((resolve, reject) => {
          try {
            c.toBlob((result) => resolve(result), type, q);
          } catch (err) {
            reject(err);
          }
        });
      });

    let blob = null;
    try {
      blob = await toBlob(canvas, "image/jpeg", quality);
    } catch (_blobError) {
      blob = null;
    }

    if (!blob) {
      throw new PropertyImageExportError("export", EXPORT_EXPORT_ERROR);
    }

    const filename = buildEditedFilename(file.name, kind);
    const FileCtor = deps.File || (typeof File !== "undefined" ? File : null);
    if (!FileCtor) {
      throw new PropertyImageExportError("export", EXPORT_EXPORT_ERROR);
    }

    try {
      return new FileCtor([blob], filename, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    } catch (_fileError) {
      throw new PropertyImageExportError("export", EXPORT_EXPORT_ERROR);
    }
  } finally {
    cleanupDecodedOnce(decoded);
  }
}

export function normalizeRotation(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const mod = ((Math.round(n) % 360) + 360) % 360;
  if (mod === 90 || mod === 180 || mod === 270) return mod;
  return 0;
}
